import React, { useEffect, useMemo, useState } from 'react';
import Button from 'src/components/Button';
import Card from 'src/components/Card';
import { ToastStoreObject } from 'src/stores/ToastStore';
import { FormFacilityMapping } from 'src/stores/FormDevelopmentStore';
import { parseError } from 'src/utils';
import {
    useAddDocumentScanTypeFormDefinition,
    useAdminDocumentScanTypes,
    useFormDefinitionDocumentScanTypes,
} from 'src/queries/useDocumentScanTypesAdmin';
import { useFormCatalog } from 'src/queries/useFormCatalog';
import { useParams } from 'react-router-dom';
import styles from './styles.module.scss';

function getMappingLabel(mapping: FormFacilityMapping) {
    const formTitle = mapping.formDefinition?.formDefnTitle || mapping.formDefinitionName || 'Unmapped form';
    const facilityName = mapping.facilityName || `Facility ${mapping.facilityId}`;
    const orgName = mapping.orgName || mapping.orgNameInternal;
    return `${facilityName} - ${formTitle}${orgName ? ` (${orgName})` : ''}`;
}

function getSupportText(allowEntityCreation: boolean, updateLinkedEntity: boolean) {
    if (allowEntityCreation && updateLinkedEntity) {
        return 'Create and update';
    }
    if (allowEntityCreation) {
        return 'Create only';
    }
    if (updateLinkedEntity) {
        return 'Update only';
    }
    return 'Configured off';
}

function CaptureAiAssociations() {
    const { formCatalogId } = useParams<any>();
    const { data: formCatalog } = useFormCatalog(formCatalogId);
    const mappings = useMemo(
        () => (formCatalog?.formFacilityMappings || []).filter((mapping) => !!mapping.formDefinitionId),
        [formCatalog?.formFacilityMappings],
    );
    const [selectedMappingId, setSelectedMappingId] = useState('');
    const selectedMapping = mappings.find((mapping) => mapping.formFacilityMappingId === selectedMappingId) || mappings[0];
    const selectedFormDefinitionId = selectedMapping?.formDefinitionId || 0;
    const { data: associations = [], isLoading, refetch } =
        useFormDefinitionDocumentScanTypes(selectedFormDefinitionId);
    const { data: scanTypes = [] } = useAdminDocumentScanTypes();
    const { mutateAsync: addAssociation, isLoading: isAdding } = useAddDocumentScanTypeFormDefinition();
    const [selectedScanTypeId, setSelectedScanTypeId] = useState('');

    useEffect(() => {
        if (!selectedMappingId && mappings.length) {
            setSelectedMappingId(mappings[0].formFacilityMappingId);
        }
    }, [mappings, selectedMappingId]);

    const associatedScanTypeIds = useMemo(
        () => new Set(associations.map((association) => association.documentScanTypeId)),
        [associations],
    );

    const availableScanTypes = useMemo(
        () =>
            scanTypes
                .filter((scanType) => scanType.supportedEntityType === 'encounter_form')
                .filter((scanType) => !associatedScanTypeIds.has(scanType.documentScanTypeId))
                .sort((left, right) => left.displayName.localeCompare(right.displayName)),
        [associatedScanTypeIds, scanTypes],
    );

    useEffect(() => {
        if (!availableScanTypes.some((scanType) => scanType.documentScanTypeId === selectedScanTypeId)) {
            setSelectedScanTypeId(availableScanTypes[0]?.documentScanTypeId || '');
        }
    }, [availableScanTypes, selectedScanTypeId]);

    const handleAddAssociation = async () => {
        if (!selectedFormDefinitionId || !selectedScanTypeId) {
            return;
        }

        try {
            await addAssociation({
                documentScanTypeId: selectedScanTypeId,
                formDefinitionId: selectedFormDefinitionId,
            });
            ToastStoreObject.show('Capture AI scan type associated');
            await refetch();
        } catch (error) {
            ToastStoreObject.show(parseError(error as any));
        }
    };

    if (!mappings.length) {
        return (
            <Card className={styles.emptyState} data-test-id="form-capture-ai-empty-mappings">
                Deploy this form to a facility before associating Capture AI scan types.
            </Card>
        );
    }

    return (
        <div className={styles.container} data-test-id="form-capture-ai-associations">
            <div className={styles.toolbar}>
                <label className={styles.field}>
                    <span className={styles.fieldLabel}>Facility Form Definition</span>
                    <select
                        className={styles.select}
                        value={selectedMapping?.formFacilityMappingId || ''}
                        onChange={(event) => setSelectedMappingId(event.target.value)}
                        data-test-id="form-capture-ai-mapping-select"
                    >
                        {mappings.map((mapping) => (
                            <option key={mapping.formFacilityMappingId} value={mapping.formFacilityMappingId}>
                                {getMappingLabel(mapping)}
                            </option>
                        ))}
                    </select>
                </label>
                <label className={styles.field}>
                    <span className={styles.fieldLabel}>Existing Encounter Form Scan Type</span>
                    <select
                        className={styles.select}
                        value={selectedScanTypeId}
                        disabled={!availableScanTypes.length}
                        onChange={(event) => setSelectedScanTypeId(event.target.value)}
                        data-test-id="form-capture-ai-scan-type-select"
                    >
                        {availableScanTypes.map((scanType) => (
                            <option key={scanType.documentScanTypeId} value={scanType.documentScanTypeId}>
                                {scanType.displayName}
                            </option>
                        ))}
                    </select>
                </label>
                <Button
                    type="primary"
                    text="Associate"
                    onClick={handleAddAssociation}
                    isLoading={isAdding}
                    disabled={!selectedScanTypeId}
                    data-test-id="form-capture-ai-associate-button"
                />
            </div>
            {isLoading ? (
                <div className={styles.emptyState}>Loading associated Capture AI scan types...</div>
            ) : associations.length ? (
                <div className={styles.table} data-test-id="form-capture-ai-associations-table">
                    <div className={styles.tableHeader}>
                        <span>Scan Type</span>
                        <span>Facility Form</span>
                        <span>Support</span>
                        <span>Status</span>
                    </div>
                    {associations.map((association) => (
                        <div className={styles.tableRow} key={association.documentScanTypeFormDefinitionId}>
                            <div>
                                <strong>{association.displayName}</strong>
                                <span>{association.scanTypeKey}</span>
                            </div>
                            <div>
                                <strong>{association.formDefinitionTitle || 'Untitled Form'}</strong>
                                <span>{association.facilityName || `Facility ${association.facilityId}`}</span>
                            </div>
                            <div className={styles.chips}>
                                <span className={styles.chip}>
                                    {getSupportText(association.allowEntityCreation, association.updateLinkedEntity)}
                                </span>
                            </div>
                            <div className={styles.chips}>
                                <span className={styles.chip}>
                                    {association.effectiveActive
                                        ? 'Available'
                                        : association.facilityAvailabilityEnabled
                                        ? 'Unavailable'
                                        : 'Disabled by facility'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className={styles.emptyState}>No Capture AI scan types are associated with this form definition.</div>
            )}
        </div>
    );
}

export default CaptureAiAssociations;
